/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { ActionCreator, AnyAction, ThunkDispatch } from '@reduxjs/toolkit';

let initializedHandler = false;
const initializedModules = new Map<string, ThunkDispatch<any, any, any>>();

/**
 * Создает обработчики для событий фокуса и статуса сети, чтобы поддержать refetchOnFocus и refetchOnReconnect.
 * От стандартного обработчика внутри setupListeners отличается тем, что умеет отправлять экшены в несколько сторов.
 *
 * @param moduleName – Название модуля.
 * @returns Метод, который нужно передать в setupListeners RTK Query API.
 */
export function createSetupListenersHandler(moduleName: string) {
    return function setupListenersHandler(
        dispatch: ThunkDispatch<any, any, any>,
        actions: {
            onFocus: ActionCreator<AnyAction>;
            onFocusLost: ActionCreator<AnyAction>;
            onOnline: ActionCreator<AnyAction>;
            onOffline: ActionCreator<AnyAction>;
        }
    ) {
        // Отправялет экшены во все сторы, которые уже подписались на события.
        function dispatchAll(action: AnyAction) {
            for (const moduleName of Array.from(initializedModules.keys())) {
                initializedModules.get(moduleName)?.(action);
            }
        }

        const handleFocus = () => dispatchAll(actions.onFocus());
        const handleFocusLost = () => dispatchAll(actions.onFocusLost());
        const handleOnline = () => dispatchAll(actions.onOnline());
        const handleOffline = () => dispatchAll(actions.onOffline());
        const handleVisibilityChange = () => {
            if (window.document.visibilityState === 'visible') {
                handleFocus();
            } else {
                handleFocusLost();
            }
        };

        if (!initializedHandler) {
            window.addEventListener('visibilitychange', handleVisibilityChange, false);
            window.addEventListener('focus', handleFocus, false);
            window.addEventListener('online', handleOnline, false);
            window.addEventListener('offline', handleOffline, false);

            initializedHandler = true;
        }

        initializedModules.set(moduleName, dispatch);

        const unsubscribe = () => {
            initializedModules.delete(moduleName);

            if (initializedModules.size === 0) {
                window.removeEventListener('focus', handleFocus);
                window.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('online', handleOnline);
                window.removeEventListener('offline', handleOffline);

                initializedHandler = false;
            }
        };

        return unsubscribe;
    };
}
